import "./segmentedControl.css";
import { useEffect, useRef, useState } from "react";
import {
  SegmentedControlButtonProps,
  SegmentedControlItemProps,
  SegmentedControlProps,
} from "./segmentedControl.props";
import { useDimensions } from "../../hooks";
import { usePermissionGuard } from "../../hooks";

const SegmentedControlButton: React.FC<SegmentedControlButtonProps> = ({
  option,
  active,
  onClick,
}) => {
  // ✅ Solo si tiene permisos definidos, se evalúan con el hook
  const { isAuthorized } = usePermissionGuard(option.withPermissions ?? []);

  // ❌ Si el usuario no tiene permisos, no se muestra este botón
  if (option.withPermissions && !isAuthorized) return null;

  return (
    <button
      key={option.id}
      data-value={option.id}
      className={`segmented-button ${active === option.id ? "active" : ""}`}
      onClick={() => onClick(option.id)}
    >
      {option.text}
    </button>
  );
};

const SegmentedControl: React.FC<SegmentedControlProps> = ({
  options,
  initial,
  onChange,
  className = "",
}) => {
  const { width } = useDimensions();
  const containerRef = useRef<HTMLDivElement>(null);

  const [active, setActive] = useState<SegmentedControlItemProps["id"]>(
    options[0]?.id ?? ""
  );
  const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 });

  useEffect(() => {
    if (containerRef.current) {
      const activeButton = containerRef.current.querySelector(
        `[data-value="${active}"]`
      ) as HTMLButtonElement;
      if (activeButton) {
        const { offsetWidth, offsetLeft } = activeButton;
        setIndicatorStyle({ width: offsetWidth, left: offsetLeft });
      }
    }
  }, [active, options, width]);

  useEffect(() => {
    if (options.length > 0) onChange?.(options[0].id);
  }, []);

  useEffect(() => {
    if (initial) setActive(initial);
  }, [initial]);

  const handleClick = (value: string) => {
    setActive(value);
    onChange?.(value);
  };

  return (
    <div className={`segmented-container ${className}`} ref={containerRef}>
      <div
        className="segmented-indicator"
        style={{
          width: `${indicatorStyle.width}px`,
          transform: `translateX(${indicatorStyle.left}px)`,
        }}
      />
      {options.map((option) => (
        <SegmentedControlButton
          key={option.id}
          option={option}
          active={active}
          onClick={handleClick}
        />
      ))}
    </div>
  );
};

export default SegmentedControl;
